import { Injectable } from '@angular/core';
import { Result, ToResult } from '@team23/ts-result';
import { AbstractSvgIconService } from '../svg-icon/abstract-svg-icon.service';
import { BehaviorSubject, Observable, concat, distinctUntilChanged, map, of, zip } from 'rxjs';
import { AbstractMapIconsService } from './abstract-map-icons.service';
import { IconStatus, fallbackIfDelayed } from '@ui/shared/util-api';

type IconResult = Result<string, IconStatus>;

const loadingResult = ToResult.fromErrorKey(IconStatus.Loading);

@Injectable()
export class MapIconsService extends AbstractMapIconsService {

    public readonly mapIcons$ = new BehaviorSubject<Map<string, IconResult>>(new Map());

    constructor(
        private readonly _svgIconService: AbstractSvgIconService,
    ) {
        super();
    }

    public mapIcon$(iconPath: string): Observable<IconResult> {
        return this.mapIcons$
            .pipe(
                map(icons => icons.get(iconPath) ?? ToResult.fromData('')),
                distinctUntilChanged(),
            );
    }

    // For all previously unmapped iconPaths, fetch the svg data and add it to the icons.
    public addIconsFromPaths(iconPaths: Array<string>, options?: { replaceExisting?: boolean }): void {
        const icons = this.mapIcons$.getValue();

        const filteredIconPaths
            = Array.from(
                new Set(iconPaths),
            )
                .filter(iconPath => options?.replaceExisting ?? !icons.get(iconPath));

        /*
         * First send a loading status for all new icons,
         * then fetch and send the backend data.
         */
        concat(
            fallbackIfDelayed(
                zip(
                    filteredIconPaths
                        .map(iconPath =>
                            this._iconDataFromPath$(iconPath)
                                .pipe(
                                    map(result => ({ iconPath, result })),
                                ),
                        ),
                ),
                filteredIconPaths.map(iconPath => ({ iconPath, result: loadingResult })),
                { gracePeriod: 0 },
            ),
        )
            .subscribe(iconSets => {
                iconSets.forEach(({ iconPath, result }) => {
                    icons.set(iconPath, result);
                });

                this.mapIcons$.next(new Map(icons));
            });
    }

    public resetIcons(): void {
        this.mapIcons$.next(new Map());
    }

    private _iconDataFromPath$(iconPath: string): Observable<IconResult> {
        return iconPath
            ? this._svgIconService.getIconSvgByPath$(iconPath)
            : of(ToResult.fromData(''));
    }

}
